/**
 * 手入力取引閲覧モーダルの受渡期間カレンダービュー (read-only)。
 *
 * spec `bilateral-trade-manual-detail.md` AC-SBM-007: 受渡期間セクションのカレンダー
 * ビューで、受渡期間内の対象日を時間帯区分ごとに色分け表示し (mock-app
 * `SupplyCapacityBilateral.razor` の受給日カレンダーに準拠)、その上に受渡カレンダー
 * 例外 (適用日 / 除外日) を重ねて表示する。カレンダー例外の登録・修正は本 AC の対象外
 * (read-only。クリックでのトグル等は実装しない)。
 *
 * `calendarDays` が空の契約 (時間帯区分も custom_bands も持たない = band 展開を
 * 定義できない契約 / 受渡期間未確定) はこのコンポーネントを描かず呼び出し側が
 * `—` に退避する (`index.tsx` の カレンダービュー 行)。受渡期間と無関係な当月
 * グリッドを「対象日ゼロ」に見せないため。
 *
 * 休日判定・時間帯区分の band 展開・受渡カレンダー例外の適用は
 * GetBilateralTrade (`calendar_days`, issue #7965) が計算済みで返す。frontend は
 * 「(時間帯区分, band_state) → 凡例文言・色」の静的表示テーブルを持つだけで、
 * 休日 / 業務判定を再計算しない。
 *
 * 色分けの静的テーブルの根拠 (mock-app `SupplyCapacityBilateral.razor` 498〜609行,
 * `.dcal-*` CSS 2895〜3075行台):
 *   - ベース: 全日対象 (単一色)
 *   - ピーク: 対象 (8-20時)/ 休日 (対象外)
 *   - オフピーク: 非休日 (0-8,20-24時)/ 休日 (24時間、警告色トーンで区別)
 *   - 標準の時間帯区分を持たない契約 (time_zone_type UNSPECIFIED、
 *     日区分×時刻レンジの custom_bands で表現する契約):
 *     mock に凡例が無いため、backend の band 展開 (supply_48_active_koma.go
 *     standardBandClassifier / ResolveSupply48ActiveKomaRule) と同じ考え方
 *     (コマ集合が非空なら対象) でこのファイルの著者が決定 (PR 本文に明記)。
 *     custom_bands の契約は「全コマ対象 / 一部のコマ対象 / 対象外」の3値
 *     (#8122)。
 */
import { useMemo, useState } from 'react'

import { DeliveryDayBandState } from '@ebalance/proto/bfftypes/v1/delivery_day_band_state_pb'
import { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import type { DeliveryCalendarDay } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { HintTooltip } from '~/components/common/HintTooltip'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { now as serverNow } from '~/models/clock'
import { jstYmd } from '~/models/dateTime'

type Props = {
  /** 契約の時間帯区分。凡例文言・色の静的テーブルの切り替えキー。 */
  timeZoneType: TradingTimeZoneType
  /** GetBilateralTrade が計算済みで返す、受渡期間内の日ごとの状態。 */
  calendarDays: DeliveryCalendarDay[]
}

const WEEKDAY_LABEL_KEYS = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
] as const

/** 日セルの描画色バケット (mock-app の dcal-dot-* / dcal-cell.dcal-* に対応)。 */
type ColorBucket = 'target' | 'targetLight' | 'nonTarget'

const BUCKET_BACKGROUND: Record<ColorBucket, string> = {
  target: 'Surface.infoLow',
  targetLight: 'Surface.warningLow',
  nonTarget: 'Surface.secondary',
}

type BandVisual = {
  state: DeliveryDayBandState
  bucket: ColorBucket
  legendKey: string
  /** 凡例文言が曜日の区分 (休日 / 平日・非休日) に言及するか。 */
  namesDayClass?: true
}

/**
 * 時間帯区分 → (band_state, 表示バケット, 凡例キー) の静的テーブル。
 * **配列の順序がそのまま凡例の表示順** (mock-app の凡例表示順)。色分けと凡例を
 * 1 つの表で持つのは、別 Record に分けると片方だけ更新して「セルは色付くのに
 * 凡例に出ない / 凡例に出るのにセルが対象外色になる」silent な不整合を招くため
 * (Partial Record なので型検査では検出できない)。
 */
const BAND_VISUALS_BY_TZ: Record<TradingTimeZoneType, BandVisual[]> = {
  [TradingTimeZoneType.BASE]: [
    { state: DeliveryDayBandState.FULL, bucket: 'target', legendKey: 'base' },
  ],
  [TradingTimeZoneType.PEAK]: [
    {
      state: DeliveryDayBandState.PARTIAL,
      bucket: 'target',
      legendKey: 'peakTarget',
    },
    {
      state: DeliveryDayBandState.NONE,
      bucket: 'nonTarget',
      legendKey: 'peakNone',
      namesDayClass: true,
    },
  ],
  [TradingTimeZoneType.OFF_PEAK]: [
    {
      state: DeliveryDayBandState.PARTIAL,
      bucket: 'target',
      legendKey: 'offPeakTarget',
      namesDayClass: true,
    },
    {
      state: DeliveryDayBandState.FULL,
      bucket: 'targetLight',
      legendKey: 'offPeakFull',
      namesDayClass: true,
    },
  ],
  // UNSPECIFIED = 標準の時間帯区分を持たず custom_bands (日区分×時刻レンジ)
  // で表現する契約 (proto コメント参照)。全コマ対象 (FULL) と一部のコマだけ
  // 対象 (PARTIAL) を区別する (#8122)。PARTIAL はオフピークの 2 系統と同じく
  // 警告色トーンで FULL と見分ける。
  [TradingTimeZoneType.UNSPECIFIED]: [
    {
      state: DeliveryDayBandState.FULL,
      bucket: 'target',
      legendKey: 'customFull',
    },
    {
      state: DeliveryDayBandState.PARTIAL,
      bucket: 'targetLight',
      legendKey: 'customPartial',
    },
    {
      state: DeliveryDayBandState.NONE,
      bucket: 'nonTarget',
      legendKey: 'customNone',
    },
  ],
}

/**
 * 受渡カレンダー例外の対象外化 (is_excluded=true) の日のセル表示。
 *
 * 対象外化は時間帯区分を問わず band_state=NONE を強制する
 * (`ResolveDeliveryCalendarDayState`) ため、時間帯区分別の NONE 文言
 * (ピーク「休日 (対象外)」など) をそのまま使うと、平日に置いた除外日を
 * 「休日」と偽って伝える。
 * 業務ルール上も文字色・曜日判定は曜日のベース判定だけを使い、例外で
 * 「休日扱いに変わった」という誤認を防ぐ決まりなので
 * (`mock-app/docs/domain-knowledge.md` §受給日例外)、曜日に言及しない
 * 「対象外」に退避する (mock-app のセル tooltip も「除外日 (例外で対象外)」
 * だけを出し休日か否かを主張しない)。凡例には出さない (例外の有無は凡例側の
 * 適用日 / 除外日 で示す)。
 */
const EXCLUDED_BY_EXCEPTION_VISUAL: Omit<BandVisual, 'state'> = {
  bucket: 'nonTarget',
  legendKey: 'excludedByException',
}

/**
 * 受渡カレンダー例外の対象化 (適用日) の日で、曜日の区分に言及する凡例文言の
 * 代わりに使う、曜日に中立な文言 (band_state ごと)。
 *
 * 適用日は backend が平日区分で band 展開する (48 点導出の forceWeekday) ため、
 * 休日に置いた適用日が「平日 (対象外)」「非休日 (0-8,20-24時)」のように
 * 曜日を偽って表示される。除外日と同じく曜日のベース判定を変えたように
 * 見せない (`mock-app/docs/domain-knowledge.md` §受給日例外)。
 */
const APPLIED_BY_EXCEPTION_LEGEND_KEY: Partial<
  Record<DeliveryDayBandState, string>
> = {
  [DeliveryDayBandState.FULL]: 'customFull',
  [DeliveryDayBandState.PARTIAL]: 'customPartial',
  [DeliveryDayBandState.NONE]: 'customNone',
}

function resolveDayVisual(
  bandVisuals: BandVisual[],
  entry: DeliveryCalendarDay,
): Omit<BandVisual, 'state'> | undefined {
  if (entry.hasException && entry.exceptionIsExcluded) {
    return EXCLUDED_BY_EXCEPTION_VISUAL
  }
  const visual = bandVisuals.find((v) => v.state === entry.bandState)
  if (visual?.namesDayClass === true && entry.hasException) {
    const neutralKey = APPLIED_BY_EXCEPTION_LEGEND_KEY[entry.bandState]
    if (neutralKey !== undefined) {
      return { bucket: visual.bucket, legendKey: neutralKey }
    }
  }
  return visual
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

function dateValue(d: { year: number; month: number; day: number }): number {
  return d.year * 10000 + d.month * 100 + d.day
}

/**
 * 月グリッドの表示対象月 (calendarDays の最初の日の月を初期値にする)。
 * 受渡日は JST の暦日で定義される業務概念のため、`firstDate` 未設定時の
 * フォールバックは端末ローカル TZ ではなく `jstYmd(serverNow())` で「今日」を求める。
 */
function initialMonth(firstDate: ProtoDate | undefined): {
  year: number
  month: number
} {
  if (firstDate !== undefined) {
    return { year: firstDate.year, month: firstDate.month }
  }
  const { year, month } = jstYmd(serverNow())
  return { year, month }
}

export const DeliveryCalendarView = ({
  timeZoneType,
  calendarDays,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const dayByDate = useMemo(() => {
    const map = new Map<number, DeliveryCalendarDay>()
    for (const d of calendarDays) {
      if (d.date !== undefined) {
        map.set(dateValue(d.date), d)
      }
    }
    return map
  }, [calendarDays])

  const [viewMonth, setViewMonth] = useState<{ year: number; month: number }>(
    () => initialMonth(calendarDays[0]?.date),
  )

  const { year, month } = viewMonth
  const firstDow = new Date(year, month - 1, 1).getDay()
  const dayCount = daysInMonth(year, month)

  const shiftMonth = (delta: number): void => {
    const next = new Date(year, month - 1 + delta, 1)
    setViewMonth({ year: next.getFullYear(), month: next.getMonth() + 1 })
  }

  const bandVisuals = BAND_VISUALS_BY_TZ[timeZoneType]
  const hasAnyException = calendarDays.some((d) => d.hasException)

  return (
    <Box borderWidth="1px" borderColor="Border.lowEmphasis" borderRadius="md">
      <HStack
        justifyContent="center"
        gap={2}
        padding={2}
        borderColor="Border.lowEmphasis"
        borderBottomWidth="1px"
      >
        <IconOnlyButton
          label={t('page:supplyBilateral.manualDetail.calendar.prevMonth')}
          onClick={() => {
            shiftMonth(-1)
          }}
          size="xs"
          variant="outline"
        >
          <FaChevronLeftRegular aria-hidden="true" />
        </IconOnlyButton>
        <Text textStyle="subSectionTitle">
          {t('page:supplyBilateral.manualDetail.calendar.monthTitle', {
            year,
            month,
          })}
        </Text>
        <IconOnlyButton
          label={t('page:supplyBilateral.manualDetail.calendar.nextMonth')}
          onClick={() => {
            shiftMonth(1)
          }}
          size="xs"
          variant="outline"
        >
          <FaChevronRightRegular aria-hidden="true" />
        </IconOnlyButton>
      </HStack>

      <Box
        gap={1}
        gridTemplateColumns="repeat(7, 1fr)"
        display="grid"
        padding={2}
      >
        {/* 曜日の見出し・日のセルは 8080 `.bi-dcal-dow` / `.bi-dcal-cell` と同じ md (14px)。
            曜日の見出しは md / 600 / mid-emphasis (tableHeader)。 */}
        {WEEKDAY_LABEL_KEYS.map((key) => (
          <Text
            key={key}
            textStyle="tableHeader"
            color="Text.midEmphasis"
            textAlign="center"
          >
            {t(`page:supplyBilateral.manualDetail.calendar.dow.${key}`)}
          </Text>
        ))}
        {Array.from({ length: firstDow }, (_, i) => (
          <Box key={`empty-${String(i)}`} />
        ))}
        {Array.from({ length: dayCount }, (_, i) => {
          const day = i + 1
          const value = dateValue({ year, month, day })
          const entry = dayByDate.get(value)

          if (entry === undefined) {
            return (
              // 受渡期間の外の日は 8080 `.bi-dcal-cell.out` と同じ low-emphasis
              // (Text.disabled は無効・placeholder 専用。 UI 辞書 tokens.md)
              <Box
                key={day}
                color="Text.lowEmphasis"
                fontSize="md"
                textAlign="center"
                background="Surface.primary"
                borderWidth="1px"
                borderColor="Border.lowEmphasis"
                borderRadius="sm"
                paddingY={1}
              >
                {day}
              </Box>
            )
          }

          const visual = resolveDayVisual(bandVisuals, entry)
          const bucket = visual?.bucket ?? 'nonTarget'
          const bandLabel =
            visual !== undefined
              ? t(
                  `page:supplyBilateral.manualDetail.calendar.legend.${visual.legendKey}`,
                )
              : ''
          const exceptionLabel = entry.hasException
            ? t(
                entry.exceptionIsExcluded
                  ? 'page:supplyBilateral.manualDetail.calendar.legend.exceptionExcluded'
                  : 'page:supplyBilateral.manualDetail.calendar.legend.exceptionIncluded',
              )
            : ''
          const title = t(
            'page:supplyBilateral.manualDetail.calendar.dayTitle',
            {
              year,
              month,
              day,
              state:
                exceptionLabel !== ''
                  ? ` (${bandLabel} / ${exceptionLabel})`
                  : ` (${bandLabel})`,
            },
          )

          return (
            // 日の状態は DS Tooltip で出す (UI 辞書 tooltip.md)。 native title は
            // Tooltip 非対応環境向けの保険として残す (HintTooltip の JSDoc)。
            <HintTooltip key={day} content={title}>
              <Box
                position="relative"
                fontSize="md"
                textAlign="center"
                background={BUCKET_BACKGROUND[bucket]}
                borderRadius="sm"
                paddingY={1}
                title={title}
              >
                {day}
                {entry.hasException && (
                  <Box
                    position="absolute"
                    top="2px"
                    right="2px"
                    width="6px"
                    height="6px"
                    background={
                      entry.exceptionIsExcluded
                        ? 'Text.negativeMid'
                        : 'Text.positiveMid'
                    }
                    borderRadius="full"
                  />
                )}
              </Box>
            </HintTooltip>
          )
        })}
      </Box>

      <HStack flexWrap="wrap" gap={3} paddingBottom={2} paddingX={2}>
        {bandVisuals.map((visual) => (
          <HStack key={visual.state} gap={1}>
            <Box
              width={2}
              height={2}
              background={BUCKET_BACKGROUND[visual.bucket]}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="full"
            />
            <Text textStyle="smallText" color="Text.midEmphasis">
              {t(
                `page:supplyBilateral.manualDetail.calendar.legend.${visual.legendKey}`,
              )}
            </Text>
          </HStack>
        ))}
        {hasAnyException && (
          <>
            <HStack gap={1}>
              <Box
                width={2}
                height={2}
                background="Text.positiveMid"
                borderRadius="full"
              />
              <Text textStyle="smallText" color="Text.midEmphasis">
                {t(
                  'page:supplyBilateral.manualDetail.calendar.legend.exceptionIncluded',
                )}
              </Text>
            </HStack>
            <HStack gap={1}>
              <Box
                width={2}
                height={2}
                background="Text.negativeMid"
                borderRadius="full"
              />
              <Text textStyle="smallText" color="Text.midEmphasis">
                {t(
                  'page:supplyBilateral.manualDetail.calendar.legend.exceptionExcluded',
                )}
              </Text>
            </HStack>
          </>
        )}
      </HStack>
    </Box>
  )
}
